import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaBoxOpen,
  FaEdit,
  FaImage,
  FaPlus,
  FaSearch,
  FaStar,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import PartnerSidebar from "../PartnerSidebar";
import "./SellProduct.css";

const STORAGE_KEY = "partnerProducts";

const initialProducts = [
  {
    id: "prd-1001",
    productName: "Live Acoustic Performance",
    productType: "Performance",
    description:
      "A professional live acoustic music performance suitable for private and corporate events.",
    sku: "MUS-ACP-001",
    category: "Live Performance",
    artSector: "Music",
    language: "Hindi",
    region: "Maharashtra",
    eventTypes: "Corporate Event, Wedding, Private Function",
    price: 18000,
    discountPrice: 16000,
    stockQuantity: 8,
    lowStockLimit: 3,
    unitType: "Event",
    unlimitedAvailability: false,
    duration: "2 Hours",
    numberOfArtists: 3,
    technicalRequirements: "Stage area, 2 microphones, PA system and power supply.",
    specifications: [
      { key: "Genre", value: "Acoustic / Bollywood" },
      { key: "Setup Time", value: "45 Minutes" },
    ],
    mainImage: "",
    galleryImages: [],
    videoLink: "",
    status: "Active",
    travelAvailability: "Available across Maharashtra",
    preparationTime: "3 Days",
    cancellationPolicy: "Free cancellation up to 7 days before the event.",
    rating: 4.9,
    createdAt: "2026-07-12T10:00:00.000Z",
    updatedAt: "2026-07-12T10:00:00.000Z",
  },
  {
    id: "prd-1002",
    productName: "Bollywood Dance Workshop",
    productType: "Workshop",
    description:
      "Interactive Bollywood dance workshop for colleges, companies and private groups.",
    sku: "DAN-BDW-002",
    category: "Workshop",
    artSector: "Dance",
    language: "Hindi",
    region: "Maharashtra",
    eventTypes: "College Event, Corporate Activity, Private Group",
    price: 8000,
    discountPrice: 7000,
    stockQuantity: 2,
    lowStockLimit: 3,
    unitType: "Session",
    unlimitedAvailability: false,
    duration: "90 Minutes",
    numberOfArtists: 2,
    technicalRequirements: "Open floor area, speakers and changing room.",
    specifications: [
      { key: "Batch Size", value: "Up to 30 participants" },
      { key: "Level", value: "Beginner Friendly" },
    ],
    mainImage: "",
    galleryImages: [],
    videoLink: "",
    status: "Active",
    travelAvailability: "Available in Pune and Mumbai",
    preparationTime: "2 Days",
    cancellationPolicy: "50% charge for cancellation within 3 days.",
    rating: 4.7,
    createdAt: "2026-07-10T10:00:00.000Z",
    updatedAt: "2026-07-10T10:00:00.000Z",
  },
  {
    id: "prd-1003",
    productName: "Signed Artist T-Shirt",
    productType: "Physical Product",
    description:
      "Premium cotton artist merchandise with official signature print.",
    sku: "MER-TSH-003",
    category: "Merchandise",
    artSector: "Music",
    language: "English",
    region: "All India",
    eventTypes: "Retail",
    price: 999,
    discountPrice: 899,
    stockQuantity: 0,
    lowStockLimit: 5,
    unitType: "Piece",
    unlimitedAvailability: false,
    duration: "",
    numberOfArtists: 0,
    technicalRequirements: "",
    specifications: [
      { key: "Material", value: "100% Cotton" },
      { key: "Sizes", value: "S, M, L, XL" },
    ],
    mainImage: "",
    galleryImages: [],
    videoLink: "",
    status: "Inactive",
    travelAvailability: "Shipping across India",
    preparationTime: "1-2 Business Days",
    cancellationPolicy: "Return accepted within 7 days if unused.",
    rating: 4.5,
    createdAt: "2026-07-08T10:00:00.000Z",
    updatedAt: "2026-07-08T10:00:00.000Z",
  },
];

const emptyProduct = {
  productName: "",
  productType: "Performance",
  description: "",
  sku: "",
  category: "",
  artSector: "",
  language: "",
  region: "",
  eventTypes: "",
  price: "",
  discountPrice: "",
  stockQuantity: "",
  lowStockLimit: 5,
  unitType: "Event",
  unlimitedAvailability: false,
  duration: "",
  numberOfArtists: "",
  technicalRequirements: "",
  specifications: [{ key: "", value: "" }],
  mainImage: "",
  galleryImages: [],
  videoLink: "",
  status: "Active",
  travelAvailability: "",
  preparationTime: "",
  cancellationPolicy: "",
  rating: 0,
};

const categoryOptions = [
  "Live Performance",
  "Workshop",
  "Merchandise",
  "Digital Product",
  "Booking Service",
  "Ticketed Event",
];

const artSectorOptions = [
  "Music",
  "Dance",
  "Theatre",
  "Visual Arts",
  "Comedy",
  "Literature",
  "Craft",
  "Mixed Arts",
];

const languageOptions = [
  "English",
  "Hindi",
  "Marathi",
  "Gujarati",
  "Tamil",
  "Telugu",
  "Bengali",
  "Punjabi",
  "Multilingual",
];

const regionOptions = [
  "All India",
  "Maharashtra",
  "Delhi NCR",
  "Karnataka",
  "Gujarat",
  "Rajasthan",
  "West Bengal",
  "Tamil Nadu",
  "Other",
];

const MyProducts = () => {
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);

  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [artSectorFilter, setArtSectorFilter] = useState("All");
  const [languageFilter, setLanguageFilter] = useState("All");
  const [regionFilter, setRegionFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [stockFilter, setStockFilter] = useState("All");
  const [minimumPrice, setMinimumPrice] = useState("");
  const [maximumPrice, setMaximumPrice] = useState("");

  useEffect(() => {
    try {
      const savedProducts = localStorage.getItem(STORAGE_KEY);

      if (savedProducts) {
        setProducts(JSON.parse(savedProducts));
      } else {
        setProducts(initialProducts);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(initialProducts));
      }
    } catch {
      setProducts(initialProducts);
      toast.error("Unable to load saved products", {
        position: "top-center",
      });
    }
  }, []);

  const saveProducts = (updatedProducts) => {
    setProducts(updatedProducts);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProducts));
    } catch {
      toast.error(
        "Unable to save products. Local storage may be full, especially if many large images were selected.",
        { position: "top-center" }
      );
    }
  };

  const getStockStatus = (product) => {
    if (product.unlimitedAvailability) {
      return "In Stock";
    }

    const stock = Number(product.stockQuantity || 0);
    const lowStockLimit = Number(product.lowStockLimit || 0);

    if (stock <= 0) {
      return "Out of Stock";
    }

    if (stock <= lowStockLimit) {
      return "Low Stock";
    }

    return "In Stock";
  };

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return products.filter((product) => {
      const searchableText = [
        product.productName,
        product.sku,
        product.category,
        product.artSector,
        product.language,
        product.region,
      ]
        .join(" ")
        .toLowerCase();

      const displayedPrice =
        Number(product.discountPrice) > 0
          ? Number(product.discountPrice)
          : Number(product.price || 0);

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);
      const matchesCategory =
        categoryFilter === "All" || product.category === categoryFilter;
      const matchesArtSector =
        artSectorFilter === "All" || product.artSector === artSectorFilter;
      const matchesLanguage =
        languageFilter === "All" || product.language === languageFilter;
      const matchesRegion =
        regionFilter === "All" || product.region === regionFilter;
      const matchesStatus =
        statusFilter === "All" || product.status === statusFilter;
      const matchesStock =
        stockFilter === "All" || getStockStatus(product) === stockFilter;
      const matchesMinimumPrice =
        minimumPrice === "" || displayedPrice >= Number(minimumPrice);
      const matchesMaximumPrice =
        maximumPrice === "" || displayedPrice <= Number(maximumPrice);

      return (
        matchesSearch &&
        matchesCategory &&
        matchesArtSector &&
        matchesLanguage &&
        matchesRegion &&
        matchesStatus &&
        matchesStock &&
        matchesMinimumPrice &&
        matchesMaximumPrice
      );
    });
  }, [
    products,
    searchText,
    categoryFilter,
    artSectorFilter,
    languageFilter,
    regionFilter,
    statusFilter,
    stockFilter,
    minimumPrice,
    maximumPrice,
  ]);

  const inventorySummary = useMemo(
    () => ({
      total: products.length,
      active: products.filter((product) => product.status === "Active").length,
      lowStock: products.filter(
        (product) => getStockStatus(product) === "Low Stock"
      ).length,
      outOfStock: products.filter(
        (product) => getStockStatus(product) === "Out of Stock"
      ).length,
    }),
    [products]
  );

  const handleOpenAddModal = () => {
    setEditingProductId(null);
    setFormData({
      ...emptyProduct,
      specifications: [{ key: "", value: "" }],
      galleryImages: [],
    });
    setIsModalOpen(true);
  };

  const handleEdit = (product) => {
    setEditingProductId(product.id);
    setFormData({
      ...product,
      specifications:
        product.specifications?.length > 0
          ? product.specifications
          : [{ key: "", value: "" }],
      galleryImages: product.galleryImages || [],
    });
    setIsModalOpen(true);
  };

  const handleDelete = (productId) => {
    const product = products.find((item) => item.id === productId);
    const shouldDelete = window.confirm(
      `Delete "${product?.productName || "this product"}"?`
    );

    if (!shouldDelete) {
      return;
    }

    saveProducts(products.filter((productItem) => productItem.id !== productId));

    toast.success("Product deleted successfully", {
      position: "top-center",
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProductId(null);
    setFormData(emptyProduct);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const readImageAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleMainImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.size > 1024 * 1024) {
      toast.error("Please select a main image smaller than 1 MB", {
        position: "top-center",
      });
      return;
    }

    try {
      const image = await readImageAsDataUrl(file);

      setFormData((current) => ({
        ...current,
        mainImage: image,
      }));
    } catch {
      toast.error("Unable to read the selected image", {
        position: "top-center",
      });
    }
  };

  const handleGalleryImagesChange = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 4);

    if (files.some((file) => file.size > 1024 * 1024)) {
      toast.error("Each gallery image must be smaller than 1 MB", {
        position: "top-center",
      });
      return;
    }

    try {
      const images = await Promise.all(files.map(readImageAsDataUrl));

      setFormData((current) => ({
        ...current,
        galleryImages: images,
      }));
    } catch {
      toast.error("Unable to read one or more gallery images", {
        position: "top-center",
      });
    }
  };

  const handleSpecificationChange = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      specifications: current.specifications.map((specification, specIndex) =>
        specIndex === index
          ? { ...specification, [field]: value }
          : specification
      ),
    }));
  };

  const addSpecification = () => {
    setFormData((current) => ({
      ...current,
      specifications: [
        ...current.specifications,
        { key: "", value: "" },
      ],
    }));
  };

  const removeSpecification = (index) => {
    setFormData((current) => ({
      ...current,
      specifications:
        current.specifications.length === 1
          ? [{ key: "", value: "" }]
          : current.specifications.filter(
              (_, specificationIndex) => specificationIndex !== index
            ),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (
      !formData.productName.trim() ||
      !formData.description.trim() ||
      !formData.sku.trim() ||
      !formData.category ||
      !formData.artSector ||
      !formData.language ||
      !formData.region ||
      Number(formData.price) < 0
    ) {
      toast.error("Please complete all required product fields", {
        position: "top-center",
      });
      return;
    }

    const cleanedSpecifications = formData.specifications.filter(
      (specification) =>
        specification.key.trim() || specification.value.trim()
    );

    const now = new Date().toISOString();

    const productPayload = {
      ...formData,
      price: Number(formData.price || 0),
      discountPrice: Number(formData.discountPrice || 0),
      stockQuantity: formData.unlimitedAvailability
        ? 0
        : Number(formData.stockQuantity || 0),
      lowStockLimit: Number(formData.lowStockLimit || 0),
      numberOfArtists: Number(formData.numberOfArtists || 0),
      specifications: cleanedSpecifications,
      updatedAt: now,
    };

    if (editingProductId) {
      const updatedProducts = products.map((product) =>
        product.id === editingProductId
          ? {
              ...product,
              ...productPayload,
              id: editingProductId,
              createdAt: product.createdAt,
            }
          : product
      );

      saveProducts(updatedProducts);

      toast.success("Product updated successfully", {
        position: "top-center",
      });
    } else {
      const newProduct = {
        ...productPayload,
        id: `prd-${Date.now()}`,
        rating: 0,
        createdAt: now,
      };

      saveProducts([newProduct, ...products]);

      toast.success("Product added successfully", {
        position: "top-center",
      });
    }

    handleCloseModal();
  };

  const clearFilters = () => {
    setSearchText("");
    setCategoryFilter("All");
    setArtSectorFilter("All");
    setLanguageFilter("All");
    setRegionFilter("All");
    setStatusFilter("All");
    setStockFilter("All");
    setMinimumPrice("");
    setMaximumPrice("");
  };

  return (
    <div className="inventory-page-layout">
      <PartnerSidebar />

      <main className="inventory-page-main">
        <div className="inventory-page-container">
          <header className="inventory-page-header">
            <div>
              
              <h1>Product Inventory Management</h1>
              <p>
                Manage artist services, performances, workshops, merchandise,
                pricing, availability, and stock.
              </p>
            </div>

            <button
              type="button"
              className="inventory-primary-button"
              onClick={handleOpenAddModal}
            >
              <FaPlus />
              Add Product
            </button>
          </header>

          <section className="inventory-summary-grid">
            <SummaryCard label="Total Products" value={inventorySummary.total} />
            <SummaryCard label="Active Products" value={inventorySummary.active} />
            <SummaryCard label="Low Stock" value={inventorySummary.lowStock} />
            <SummaryCard
              label="Out of Stock"
              value={inventorySummary.outOfStock}
            />
          </section>

          <section className="inventory-card inventory-filter-card">
            <div className="inventory-filter-header">
              <div>
                <h2>Search & Filters</h2>
                <p>Filter the inventory using product and market details.</p>
              </div>

              <button
                type="button"
                className="inventory-text-button"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            </div>

            <div className="inventory-filter-grid">
              <label className="inventory-search-field">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search product, SKU, category..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </label>

              <FilterSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                label="Category"
                options={categoryOptions}
              />

              <FilterSelect
                value={artSectorFilter}
                onChange={setArtSectorFilter}
                label="Art Sector"
                options={artSectorOptions}
              />

              <FilterSelect
                value={languageFilter}
                onChange={setLanguageFilter}
                label="Language"
                options={languageOptions}
              />

              <FilterSelect
                value={regionFilter}
                onChange={setRegionFilter}
                label="Region"
                options={regionOptions}
              />

              <FilterSelect
                value={stockFilter}
                onChange={setStockFilter}
                label="Stock"
                options={["In Stock", "Low Stock", "Out of Stock"]}
              />

              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                label="Status"
                options={["Active", "Inactive", "Draft"]}
              />

              <label className="inventory-price-filter">
                <span>Minimum Price</span>
                <input
                  type="number"
                  min="0"
                  placeholder="₹ Min"
                  value={minimumPrice}
                  onChange={(event) => setMinimumPrice(event.target.value)}
                />
              </label>

              <label className="inventory-price-filter">
                <span>Maximum Price</span>
                <input
                  type="number"
                  min="0"
                  placeholder="₹ Max"
                  value={maximumPrice}
                  onChange={(event) => setMaximumPrice(event.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="inventory-card inventory-table-card">
            <div className="inventory-table-header">
              <div>
                <h2>Product Inventory</h2>
                <p>
                  Showing {filteredProducts.length} of {products.length} products
                </p>
              </div>
            </div>

            <div className="inventory-table-wrapper">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Art Sector</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Stock Tag</th>
                    <th>Rating</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => {
                      const stockStatus = getStockStatus(product);
                      const sellingPrice =
                        Number(product.discountPrice) > 0
                          ? Number(product.discountPrice)
                          : Number(product.price || 0);

                      return (
                        <tr key={product.id}>
                          <td>
                            <div className="inventory-product-cell">
                              <div className="inventory-product-image">
                                {product.mainImage ? (
                                  <img
                                    src={product.mainImage}
                                    alt={product.productName}
                                  />
                                ) : (
                                  <FaBoxOpen />
                                )}
                              </div>

                              <div>
                                <strong>{product.productName}</strong>
                                <small>{product.sku}</small>
                              </div>
                            </div>
                          </td>

                          <td>{product.category}</td>
                          <td>{product.artSector}</td>

                          <td>
                            <div className="inventory-price-cell">
                              <strong>
                                ₹{sellingPrice.toLocaleString("en-IN")}
                              </strong>

                              {Number(product.discountPrice) > 0 &&
                                Number(product.discountPrice) <
                                  Number(product.price) && (
                                  <small>
                                    ₹
                                    {Number(product.price).toLocaleString(
                                      "en-IN"
                                    )}
                                  </small>
                                )}
                            </div>
                          </td>

                          <td>
                            {product.unlimitedAvailability
                              ? "Unlimited"
                              : `${product.stockQuantity} ${product.unitType}`}
                          </td>

                          <td>
                            <span
                              className={`inventory-stock-tag ${stockStatus
                                .toLowerCase()
                                .replaceAll(" ", "-")}`}
                            >
                              {stockStatus}
                            </span>
                          </td>

                          <td>
                            <span className="inventory-rating">
                              <FaStar />
                              {Number(product.rating || 0).toFixed(1)}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`inventory-product-status ${product.status.toLowerCase()}`}
                            >
                              {product.status}
                            </span>
                          </td>

                          <td>
                            <div className="inventory-actions">
                              <button
                                type="button"
                                className="inventory-action-button edit"
                                onClick={() => handleEdit(product)}
                                aria-label={`Edit ${product.productName}`}
                              >
                                <FaEdit />
                              </button>

                              <button
                                type="button"
                                className="inventory-action-button delete"
                                onClick={() => handleDelete(product.id)}
                                aria-label={`Delete ${product.productName}`}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="inventory-empty-state">
                          <FaBoxOpen />
                          <strong>No products found</strong>
                          <p>Change the filters or add a new product.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>

      {isModalOpen && (
        <div className="product-modal-backdrop" role="presentation">
          <div
            className="product-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="product-modal-title"
          >
            <div className="product-modal-header">
              <div>
                <span>Product Inventory</span>
                <h2 id="product-modal-title">
                  {editingProductId ? "Edit Product" : "Add New Product"}
                </h2>
              </div>

              <button
                type="button"
                className="product-modal-close"
                onClick={handleCloseModal}
                aria-label="Close product form"
              >
                <FaTimes />
              </button>
            </div>

            <form className="product-modal-form" onSubmit={handleSubmit}>
              <ProductFormSection title="Basic Details">
                <ProductField
                  label="Product Name"
                  name="productName"
                  value={formData.productName}
                  onChange={handleInputChange}
                  required
                />

                <ProductSelect
                  label="Product Type"
                  name="productType"
                  value={formData.productType}
                  onChange={handleInputChange}
                  options={[
                    "Physical Product",
                    "Booking Service",
                    "Workshop",
                    "Performance",
                    "Digital Product",
                  ]}
                />

                <ProductField
                  label="SKU / Product Code"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  required
                />

                <ProductField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  textarea
                  fullWidth
                  required
                />
              </ProductFormSection>

              <ProductFormSection title="Classification">
                <ProductSelect
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  options={categoryOptions}
                  placeholder="Select category"
                  required
                />

                <ProductSelect
                  label="Art Sector"
                  name="artSector"
                  value={formData.artSector}
                  onChange={handleInputChange}
                  options={artSectorOptions}
                  placeholder="Select art sector"
                  required
                />

                <ProductSelect
                  label="Language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  options={languageOptions}
                  placeholder="Select language"
                  required
                />

                <ProductSelect
                  label="Region"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  options={regionOptions}
                  placeholder="Select region"
                  required
                />

                <ProductField
                  label="Suitable Event Types"
                  name="eventTypes"
                  value={formData.eventTypes}
                  onChange={handleInputChange}
                  placeholder="Corporate events, weddings, festivals..."
                  fullWidth
                />
              </ProductFormSection>

              <ProductFormSection title="Pricing & Stock">
                <ProductField
                  label="Price"
                  name="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                />

                <ProductField
                  label="Discount Price"
                  name="discountPrice"
                  type="number"
                  min="0"
                  value={formData.discountPrice}
                  onChange={handleInputChange}
                />

                <ProductField
                  label="Stock Quantity"
                  name="stockQuantity"
                  type="number"
                  min="0"
                  value={formData.stockQuantity}
                  onChange={handleInputChange}
                  disabled={formData.unlimitedAvailability}
                />

                <ProductField
                  label="Low-stock Limit"
                  name="lowStockLimit"
                  type="number"
                  min="0"
                  value={formData.lowStockLimit}
                  onChange={handleInputChange}
                />

                <ProductSelect
                  label="Unit Type"
                  name="unitType"
                  value={formData.unitType}
                  onChange={handleInputChange}
                  options={[
                    "Piece",
                    "Ticket",
                    "Hour",
                    "Session",
                    "Event",
                    "Day",
                    "Package",
                  ]}
                />

                <label className="product-checkbox-field">
                  <input
                    type="checkbox"
                    name="unlimitedAvailability"
                    checked={formData.unlimitedAvailability}
                    onChange={handleInputChange}
                  />
                  <span>
                    <strong>Unlimited Availability</strong>
                    <small>
                      Use for services or performances without fixed stock.
                    </small>
                  </span>
                </label>
              </ProductFormSection>

              <ProductFormSection title="Product Details">
                <ProductField
                  label="Duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder="Example: 2 Hours"
                />

                <ProductField
                  label="Number of Artists"
                  name="numberOfArtists"
                  type="number"
                  min="0"
                  value={formData.numberOfArtists}
                  onChange={handleInputChange}
                />

                <ProductField
                  label="Technical Requirements"
                  name="technicalRequirements"
                  value={formData.technicalRequirements}
                  onChange={handleInputChange}
                  textarea
                  fullWidth
                />

                <div className="product-specifications full-width">
                  <div className="product-specifications-header">
                    <div>
                      <strong>Specifications</strong>
                      <small>Add optional key-value product details.</small>
                    </div>

                    <button type="button" onClick={addSpecification}>
                      <FaPlus />
                      Add Specification
                    </button>
                  </div>

                  {formData.specifications.map((specification, index) => (
                    <div
                      className="product-specification-row"
                      key={`specification-${index}`}
                    >
                      <input
                        type="text"
                        placeholder="Key, e.g. Genre"
                        value={specification.key}
                        onChange={(event) =>
                          handleSpecificationChange(
                            index,
                            "key",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        placeholder="Value, e.g. Classical"
                        value={specification.value}
                        onChange={(event) =>
                          handleSpecificationChange(
                            index,
                            "value",
                            event.target.value
                          )
                        }
                      />

                      <button
                        type="button"
                        onClick={() => removeSpecification(index)}
                        aria-label="Remove specification"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </ProductFormSection>

              <ProductFormSection title="Media">
                <label className="product-file-field">
                  <span>Main Image</span>
                  <div>
                    <FaImage />
                    <strong>
                      {formData.mainImage
                        ? "Main image selected"
                        : "Select main image"}
                    </strong>
                    <small>JPG, PNG or WEBP. Maximum 1 MB.</small>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageChange}
                  />
                </label>

                <label className="product-file-field">
                  <span>Gallery Images</span>
                  <div>
                    <FaImage />
                    <strong>
                      {formData.galleryImages.length > 0
                        ? `${formData.galleryImages.length} images selected`
                        : "Select up to 4 images"}
                    </strong>
                    <small>Each image must be under 1 MB.</small>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImagesChange}
                  />
                </label>

                <ProductField
                  label="Video or Portfolio Link"
                  name="videoLink"
                  value={formData.videoLink}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  fullWidth
                />
              </ProductFormSection>

              <ProductFormSection title="Availability & Status">
                <ProductSelect
                  label="Product Status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  options={["Active", "Inactive", "Draft"]}
                />

                <ProductField
                  label="Travel Availability"
                  name="travelAvailability"
                  value={formData.travelAvailability}
                  onChange={handleInputChange}
                  placeholder="Example: Available across India"
                />

                <ProductField
                  label="Preparation Time"
                  name="preparationTime"
                  value={formData.preparationTime}
                  onChange={handleInputChange}
                  placeholder="Example: 3 Days"
                />

                <ProductField
                  label="Cancellation Policy"
                  name="cancellationPolicy"
                  value={formData.cancellationPolicy}
                  onChange={handleInputChange}
                  textarea
                  fullWidth
                />
              </ProductFormSection>

              <div className="product-modal-footer">
                <button
                  type="button"
                  className="inventory-secondary-button"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>

                <button type="submit" className="inventory-primary-button">
                  {editingProductId ? "Update Product" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <article className="inventory-summary-card">
    <span>{label}</span>
    <strong>{value}</strong>
  </article>
);

const FilterSelect = ({ value, onChange, label, options }) => (
  <label className="inventory-filter-select">
    <span>{label}</span>
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="All">All {label}</option>
      {options.map((option) => (
        <option value={option} key={option}>
          {option}
        </option>
      ))}
    </select>
  </label>
);

const ProductFormSection = ({ title, children }) => (
  <section className="product-form-section">
    <h3>{title}</h3>
    <div className="product-form-grid">{children}</div>
  </section>
);

const ProductField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  textarea = false,
  fullWidth = false,
  required = false,
  placeholder = "",
  min,
  disabled = false,
}) => (
  <label className={`product-form-field ${fullWidth ? "full-width" : ""}`}>
    <span>
      {label}
      {required && <sup>*</sup>}
    </span>

    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        required={required}
        placeholder={placeholder}
        disabled={disabled}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        min={min}
        disabled={disabled}
      />
    )}
  </label>
);

const ProductSelect = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  required = false,
}) => (
  <label className="product-form-field">
    <span>
      {label}
      {required && <sup>*</sup>}
    </span>

    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option value={option} key={option}>
          {option}
        </option>
      ))}
    </select>
  </label>
);

export default MyProducts;